/**
 * studio/src/lib/appwrite.ts
 *
 * Appwrite Web SDK v1.9.x client for the creator dashboard SPA.
 * Uses: Client, Account, Databases, Storage, Functions, Query, ID, Permission, Role.
 *
 * Setup: Add a Web platform in Appwrite Console for studio.honestpitches.com
 * before using in production.
 */

import {
  Client,
  Account,
  Databases,
  Storage,
  Functions,
  ExecutionMethod,
  Query,
  ID,
  Permission,
  Role,
  type Models,
} from "appwrite";

// ─── Client singleton ──────────────────────────────────────────────────────────

export const client = new Client();

const endpoint = (import.meta.env.VITE_APPWRITE_ENDPOINT as string) ?? "http://localhost/v1";
const projectId = (import.meta.env.VITE_APPWRITE_PROJECT_ID as string) ?? "";
const databaseId = (import.meta.env.VITE_APPWRITE_DATABASE_ID as string) ?? "honest_pitches";

client.setEndpoint(endpoint).setProject(projectId);

// ─── Service exports ─────────────────────────────────────────────────────────

export const account = new Account(client);
export const databases = new Databases(client);
export const storage = new Storage(client);
export const functionsSvc = new Functions(client);

// ─── Database ID ─────────────────────────────────────────────────────────────

export { databaseId };

// ─── Collection IDs ───────────────────────────────────────────────────────────

export const COLLS = {
  creators: "creators",
  pitches: "pitches",
  pitch_listings: "pitch_listings",
  pitch_disclosures: "pitch_disclosures",
  pitch_pros_cons: "pitch_pros_cons",
  claims: "claims",
  votes_hype_reality: "votes_hype_reality",
  flags: "flags",
  conversion_events: "conversion_events",
  subscriptions: "subscriptions",
  audit_log: "audit_log",
  stripe_events: "stripe_events",
  build_jobs: "build_jobs",
} as const;

// ─── Auth ───────────────────────────────────────────────────────────────────

/**
 * Send a magic link to the user's email.
 * After they click the link, use createSessionFromSecret() to log in.
 */
export async function sendMagicLink(email: string, redirectUrl: string) {
  return account.createMagicURLToken(ID.unique(), email, redirectUrl);
}

/**
 * Complete magic-link login using the secret from the URL query params.
 */
export async function createSessionFromSecret(userId: string, secret: string) {
  return account.createSession(userId, secret);
}

export async function getCurrentUser(): Promise<Models.User<Record<string, unknown>>> {
  return account.get();
}

export async function deleteCurrentSession() {
  return account.deleteSession("current");
}

export async function listUserSessions() {
  return account.listSessions();
}

// ─── Pitches ─────────────────────────────────────────────────────────────────

export async function listPitchesForCreator(creatorId: string, status?: string) {
  const queries = [Query.equal("creatorId", creatorId)];
  if (status) queries.push(Query.equal("status", status));
  return databases.listDocuments(databaseId, COLLS.pitches, queries);
}

export async function getPitch(pitchId: string) {
  return databases.getDocument(databaseId, COLLS.pitches, pitchId);
}

export async function getPitchBySlug(slug: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitches, [
    Query.equal("slug", slug),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function getPitchByHpCode(hpCode: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitches, [
    Query.equal("hpCode", hpCode),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

/** Current canonical pitch for an HP code (not superseded by a newer replacement). */
export async function getCanonicalPitchByHpCode(hpCode: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitches, [
    Query.equal("hpCode", hpCode),
    Query.isNull("replacedByPitchId"),
    Query.orderDesc("$createdAt"),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function createPitch(data: Record<string, unknown>) {
  return databases.createDocument(
    databaseId,
    COLLS.pitches,
    ID.unique(),
    data,
    [
      Permission.read(Role.users()),
      Permission.update(Role.user(data["creatorId"] as string)),
      Permission.delete(Role.user(data["creatorId"] as string)),
    ]
  );
}

/** Shared write path for the pitch-write Appwrite Function. */
export const PITCH_WRITE_FUNCTION_ID = "pitch-write";

/** @deprecated use PITCH_WRITE_FUNCTION_ID */
export const PITCH_CREATE_FUNCTION_ID = PITCH_WRITE_FUNCTION_ID;

export const PITCH_TRANSITION_FUNCTION_ID = "pitch-transition";

/**
 * Invoke an Appwrite Function synchronously via the SDK.
 */
async function invokeFunction(
  functionId: string,
  body: Record<string, unknown>,
  path = "/",
  method: ExecutionMethod = ExecutionMethod.POST,
): Promise<Record<string, unknown>> {
  const execution = await functionsSvc.createExecution(
    functionId,
    JSON.stringify(body),
    false,
    path,
    method,
    { "content-type": "application/json" },
  );

  if (execution.responseStatusCode >= 400) {
    const errorBody = safeJson(execution.responseBody) as { error?: string } | null;
    throw new Error(
      errorBody?.error ?? execution.errors ?? `${functionId} failed`,
    );
  }
  return (safeJson(execution.responseBody) ?? {}) as Record<string, unknown>;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Create a pitch via pitch-write (POST /). */
export async function createPitchViaFunction(data: Record<string, unknown>) {
  return invokeFunction(PITCH_WRITE_FUNCTION_ID, data);
}

/** Update a pitch via pitch-write (PATCH /:id). */
export async function updatePitchViaFunction(
  pitchId: string,
  data: Record<string, unknown>,
) {
  return invokeFunction(PITCH_WRITE_FUNCTION_ID, data, `/${pitchId}`, ExecutionMethod.PATCH);
}

/** Flip pitch to public test-share (POST /:id/test). */
export async function flipPitchToTest(pitchId: string) {
  return invokeFunction(PITCH_WRITE_FUNCTION_ID, {}, `/${pitchId}/test`);
}

/** Start paid publish flow (POST /:id/pay). */
export async function flipPitchToPayment(pitchId: string) {
  return invokeFunction(PITCH_WRITE_FUNCTION_ID, {}, `/${pitchId}/pay`);
}

/** Promote a replacement via pitch-transition. */
export async function promotePitchReplacement(
  replacementId: string,
  predecessorId: string,
) {
  return invokeFunction(
    PITCH_TRANSITION_FUNCTION_ID,
    { predecessorId },
    `/${replacementId}/promote-replacement`,
  );
}

/** Archive a pitch via pitch-transition. */
export async function archivePitchViaFunction(pitchId: string) {
  return invokeFunction(
    PITCH_TRANSITION_FUNCTION_ID,
    {},
    `/${pitchId}/archive`,
  );
}

/** Reactivate an archived pitch via pitch-transition. */
export async function reactivatePitchViaFunction(pitchId: string) {
  return invokeFunction(
    PITCH_TRANSITION_FUNCTION_ID,
    {},
    `/${pitchId}/reactivate`,
  );
}

/** Create a Stripe checkout session via the stripe-checkout Appwrite Function. */
export async function createStripeCheckout(
  data: Record<string, unknown>,
): Promise<{ url: string | null }> {
  return (await invokeFunction("stripe-checkout", data)) as { url: string | null };
}

export async function updatePitch(
  pitchId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.pitches, pitchId, data);
}

export async function deletePitch(pitchId: string) {
  return databases.deleteDocument(databaseId, COLLS.pitches, pitchId);
}

/**
 * Promote a staged replacement via pitch-transition.
 * @deprecated prefer promotePitchReplacement — kept for one release.
 */
export async function promoteReplacement(
  replacementId: string,
  predecessorId: string,
) {
  return promotePitchReplacement(replacementId, predecessorId);
}

/** Enqueue a static rebuild for a pitch slug (build_jobs). */
export async function enqueueBuildJob(
  pitchId: string,
  slug: string,
  action: "publish" | "expire" | "delete" = "publish"
) {
  return databases.createDocument(databaseId, COLLS.build_jobs, ID.unique(), {
    pitchId,
    slug,
    action,
    status: "pending",
    enqueuedAt: new Date().toISOString(),
  });
}

// ─── Pitch listings ───────────────────────────────────────────────────────────

export async function createListing(data: Record<string, unknown>) {
  return databases.createDocument(
    databaseId,
    COLLS.pitch_listings,
    ID.unique(),
    data,
    [Permission.read(Role.user(data["creatorId"] as string))]
  );
}

export async function getActiveListingForPitch(pitchId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitch_listings, [
    Query.equal("pitchId", pitchId),
    Query.equal("status", "active"),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function listListingsForCreator(creatorId: string) {
  return databases.listDocuments(databaseId, COLLS.pitch_listings, [
    Query.equal("creatorId", creatorId),
    Query.orderDesc("paidAt"),
    Query.limit(50),
  ]);
}

export async function updateListing(
  listingId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.pitch_listings, listingId, data);
}

// ─── Disclosures ──────────────────────────────────────────────────────────────

export async function listDisclosures(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.pitch_disclosures, [
    Query.equal("pitchId", pitchId),
  ]);
}

export async function createDisclosure(
  pitchId: string,
  data: Record<string, unknown>
) {
  return databases.createDocument(databaseId, COLLS.pitch_disclosures, ID.unique(), {
    pitchId,
    ...data,
  });
}

export async function updateDisclosure(
  disclosureId: string,
  data: Record<string, unknown>,
) {
  return databases.updateDocument(databaseId, COLLS.pitch_disclosures, disclosureId, data);
}

/** First disclosure row for a pitch, if any. */
export async function getDisclosureForPitch(pitchId: string) {
  const res = await listDisclosures(pitchId);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function deleteDisclosure(disclosureId: string) {
  return databases.deleteDocument(databaseId, COLLS.pitch_disclosures, disclosureId);
}

// ─── Pros / Cons ─────────────────────────────────────────────────────────────

export async function listProsCons(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.pitch_pros_cons, [
    Query.equal("pitchId", pitchId),
  ]);
}

export async function createProCon(
  pitchId: string,
  data: Record<string, unknown>
) {
  return databases.createDocument(databaseId, COLLS.pitch_pros_cons, ID.unique(), {
    pitchId,
    ...data,
  });
}

export async function deleteProCon(id: string) {
  return databases.deleteDocument(databaseId, COLLS.pitch_pros_cons, id);
}

// ─── Claims ─────────────────────────────────────────────────────────────────

export async function listClaims(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.claims, [
    Query.equal("pitchId", pitchId),
  ]);
}

export async function createClaim(pitchId: string, data: Record<string, unknown>) {
  return databases.createDocument(databaseId, COLLS.claims, ID.unique(), {
    pitchId,
    ...data,
  });
}

export async function updateClaim(
  claimId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.claims, claimId, data);
}

// ─── Votes ───────────────────────────────────────────────────────────────────

export async function castVote(data: {
  pitchId: string;
  hypeScore: number;
  realityScore: number;
  anonHash?: string;
}) {
  return databases.createDocument(
    databaseId,
    COLLS.votes_hype_reality,
    ID.unique(),
    data
  );
}

export async function getVotesForPitch(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.votes_hype_reality, [
    Query.equal("pitchId", pitchId),
  ]);
}

// ─── Conversion events ───────────────────────────────────────────────────────

export async function logConversion(data: {
  pitchId: string;
  eventType: string;
  eventPositionSeconds?: number;
  valueCents?: number;
  referrer?: string;
  country?: string;
  device?: string;
}) {
  return databases.createDocument(
    databaseId,
    COLLS.conversion_events,
    ID.unique(),
    { ...data, createdAt: new Date().toISOString() }
  );
}

// ─── Flags ───────────────────────────────────────────────────────────────────

export async function createFlag(data: Record<string, unknown>) {
  return databases.createDocument(databaseId, COLLS.flags, ID.unique(), data);
}

// ─── Creators ────────────────────────────────────────────────────────────────

export async function getOrCreateCreator(userId: string, email: string) {
  const res = await databases.listDocuments(databaseId, COLLS.creators, [
    Query.equal("userId", userId),
    Query.limit(1),
  ]);
  if (res.documents[0]) return res.documents[0];

  return databases.createDocument(databaseId, COLLS.creators, ID.unique(), {
    userId,
    email,
    plan: "free",
  });
}

export async function getCreatorByUserId(userId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.creators, [
    Query.equal("userId", userId),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function updateCreator(
  creatorId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.creators, creatorId, data);
}

// ─── Subscriptions ────────────────────────────────────────────────────────────

export async function getActiveSubscription(creatorId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.subscriptions, [
    Query.equal("creatorId", creatorId),
    Query.equal("status", "active"),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function createSubscription(data: Record<string, unknown>) {
  return databases.createDocument(databaseId, COLLS.subscriptions, ID.unique(), data);
}

export async function updateSubscription(
  subId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.subscriptions, subId, data);
}

// ─── Storage ────────────────────────────────────────────────────────────────

export async function uploadFile(
  bucketId: string,
  file: File,
  onProgress?: (pct: number) => void
) {
  return storage.createFile(bucketId, ID.unique(), file);
}

export function getFilePreviewUrl(
  bucketId: string,
  fileId: string,
  width?: number,
  height?: number
) {
  const params = new URLSearchParams();
  params.set("project", import.meta.env.VITE_APPWRITE_PROJECT_ID as string);
  if (width) params.set("width", String(width));
  if (height) params.set("height", String(height));
  return `${import.meta.env.VITE_APPWRITE_ENDPOINT}/storage/buckets/${bucketId}/files/${fileId}/preview?${params.toString()}`;
}

// ─── Audit log helper ────────────────────────────────────────────────────────

/** Pitch audit rows for the signed-in creator (human or agent SDK path). */
export async function listAgentActivityForUser(userId: string, limit = 50) {
  const creator = await getCreatorByUserId(userId);
  if (!creator) {
    return { documents: [], total: 0 };
  }
  return databases.listDocuments(databaseId, COLLS.audit_log, [
    Query.equal("metadata.creatorId", creator.$id as string),
    Query.orderDesc("$createdAt"),
    Query.limit(limit),
  ]);
}

export async function logAudit(
  action: string,
  targetType: string,
  targetId: string,
  metadata: Record<string, unknown> = {},
  actorUserId?: string
) {
  return databases.createDocument(databaseId, COLLS.audit_log, ID.unique(), {
    action,
    targetType,
    targetId,
    metadata,
    actorUserId: actorUserId ?? null,
    createdAt: new Date().toISOString(),
  });
}

// ─── Stripe webhook idempotency ─────────────────────────────────────────────

export async function isStripeEventProcessed(stripeEventId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.stripe_events, [
    Query.equal("stripeEventId", stripeEventId),
    Query.limit(1),
  ]);
  return res.documents.length > 0;
}

export async function markStripeEventProcessed(stripeEventId: string) {
  return databases.createDocument(
    databaseId,
    COLLS.stripe_events,
    ID.unique(),
    {
      stripeEventId,
      processedAt: new Date().toISOString(),
    }
  );
}
